import React, { useState } from "react";
import { Link } from "react-router-dom";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { RANGE_OPTIONS, greeting } from "./format";

export function Panel({ title, subtitle, action, children, className = "", index = 0, as: Tag = "section" }) {
  return (
    <Tag className={`dk-panel dk-rise ${className}`} style={{ "--i": index }}>
      {(title || action) && (
        <header className="dk-panel__head">
          <div>
            {title && <h2 className="dk-panel__title">{title}</h2>}
            {subtitle && <p className="dk-panel__subtitle">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </Tag>
  );
}

/** Date range filter: one row above everything it scopes. */
export function RangeTabs({ value, onChange, label = "Time range" }) {
  return (
    <div className="dk-range" role="radiogroup" aria-label={label}>
      {RANGE_OPTIONS.map((r) => (
        <button key={r.value} type="button" role="radio" aria-checked={value === r.value} className={value === r.value ? "is-active" : ""} onClick={() => onChange(r.value)}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

function Thumb({ src, alt }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="dk-barlist__thumb">
      {src && !failed ? <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} /> : <ImageNotSupportedOutlinedIcon fontSize="small" />}
    </span>
  );
}

/**
 * Ranked horizontal bars with the value at each bar's tip (every value is
 * labelled, so no tooltip is needed). items: [{ id, label, value, meta, image, to }]
 */
export function BarList({ items, formatValue = (v) => v, emptyText = "Nothing to rank yet." }) {
  if (!items?.length) return <p className="dk-empty">{emptyText}</p>;
  const max = Math.max(...items.map((i) => i.value), 0) || 1;
  return (
    <ol className="dk-barlist">
      {items.map((item, i) => {
        const body = (
          <>
            <Thumb src={item.image} alt="" />
            <span className="dk-barlist__main">
              <span className="dk-barlist__row">
                <span className="dk-barlist__label" title={item.label}>
                  {item.label}
                </span>
                <strong className="dk-barlist__value">{formatValue(item.value)}</strong>
              </span>
              <span className="dk-barlist__track" aria-hidden="true">
                <span className="dk-barlist__fill" style={{ "--w": `${Math.max(2, (item.value / max) * 100)}%`, "--i": i }} />
              </span>
              {item.meta && <span className="dk-barlist__meta">{item.meta}</span>}
            </span>
          </>
        );
        return (
          <li key={item.id ?? i}>
            {item.to ? (
              <Link to={item.to} className="dk-barlist__item">
                {body}
              </Link>
            ) : (
              <div className="dk-barlist__item">{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** Ratio against a limit; the track is a lighter step of the fill's hue. */
export function Meter({ label, valueText, ratio, caption }) {
  const pct = Math.max(0, Math.min(1, Number(ratio) || 0)) * 100;
  return (
    <div className="dk-meter">
      <div className="dk-meter__row">
        <span className="dk-meter__label">{label}</span>
        <strong>{valueText}</strong>
      </div>
      <div className="dk-meter__track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-valuetext={valueText}>
        <span className="dk-meter__fill" style={{ "--w": `${pct}%` }} />
      </div>
      {caption && <p className="dk-meter__caption">{caption}</p>}
    </div>
  );
}

/**
 * Dashboard header: greeting, context line, actions and one hero figure.
 */
export function DashboardHero({ eyebrow, name, subtitle, actions, figure, figureLabel, figureNote, children }) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  return (
    <section className="dk-hero dk-rise" style={{ "--i": 0 }}>
      <div className="dk-hero__glow" aria-hidden="true" />
      <div className="dk-hero__content">
        <p className="dk-hero__eyebrow">
          {eyebrow} <span aria-hidden="true">·</span> {today}
        </p>
        <h1 className="dk-hero__title">
          {greeting()}
          {name ? `, ${name}` : ""}
          <span className="dk-hero__wave" aria-hidden="true">
            {" "}
            👋
          </span>
        </h1>
        {subtitle && <p className="dk-hero__subtitle">{subtitle}</p>}
        {actions && <div className="dk-hero__actions">{actions}</div>}
        {children}
      </div>
      {figure !== undefined && (
        <div className="dk-hero__figure">
          <p className="dk-hero__figure-label">{figureLabel}</p>
          <p className="dk-hero__figure-value">{figure}</p>
          {figureNote && <div className="dk-hero__figure-note">{figureNote}</div>}
        </div>
      )}
    </section>
  );
}

export function TileSkeleton({ count = 4 }) {
  return (
    <div className="dk-tiles" aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="dk-tile dk-skeleton" />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="dk-error" role="alert">
      <p>{message}</p>
      {onRetry && (
        <button type="button" className="dk-btn dk-btn--ghost" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
